/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — Provider Web
 * Purpose      : REST client for MessageTemplateApi (provider-api). Returns a
 *                discriminated result so a failed fetch can never be mistaken for
 *                an empty one.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * Comms draft/   | 05-Aug-2026 | Claude         | Adds fetchManageCatalog() (GET /manage — the
 * publish                                        provider-admin DRAFT/UNDER_REVIEW/APPROVED/
 *                                                 PUBLISHED list, NOT the tenant-facing live
 *                                                 catalog gap note 1 used to describe),
 *                                                 publishTemplate() (POST /{uid}/publish), and
 *                                                 editTemplate() (PUT /{uid}/edit — in-place edit
 *                                                 of a provider-owned DRAFT/APPROVED/REJECTED/
 *                                                 CHANGES_REQUESTED row, same version).
 * Comms manage-  | 06-Aug-2026 | Claude         | Backend fix, not a frontend change: GET /manage
 * catalog gap                                     now also returns CHANGES_REQUESTED rows, then
 *                                                 (same day, a follow-up request) REJECTED rows
 *                                                 too — a tenant resubmission sent back for
 *                                                 rework, or a rejected one, used to be invisible
 *                                                 on every provider-web screen. See
 *                                                 MessageTemplateMasterRepository
 *                                                 #findManageCatalog's own javadoc. This file's
 *                                                 doc comment updated to match, twice;
 *                                                 fetchManageCatalog itself needed no code change
 *                                                 either time — it already returns whatever the
 *                                                 endpoint sends.
 * SMS split PR-2 | 07-Aug-2026 | Claude         | Adds setTemplateCategory() (PATCH .../category)
 *                                                 and the executePatchData import it needs.
 * 30639          | 14-Sep-2026 | Shabnam        | Added business-flow template categories and
 *                                                 channel/category default selection for
 *                                                 Estimate Link templates.
 */

import { executeGetData, executePatchData, executePostData, executePutData } from '../services/ApiBase';
import { ConfigUrl } from '../auth/OidcConfig';
import {
  PreviewResponse, TemplateCategoryOption, TemplateSubmit, TemplateSummary, TemplateUsage,
} from './CommunicationTypes';

/**
 * ============================ WHY NOT `res.ok ? data : []` ============================
 *
 * Every other service in this app ends in `return res.ok ? (res.data as X[]) : [];`.
 * This one deliberately does NOT, and must not be "made consistent" with them.
 *
 * That idiom turns a 500, a 403 and a dropped connection into an empty array. On a review
 * queue that reads to the provider as "there is nothing to review" — while tenant change
 * requests sit pending and unseen. This exact failure mode has recurred on this project,
 * so the queue's transport layer refuses to express it: callers get a tagged result and
 * physically cannot render an error as emptiness without writing the word `error`.
 *
 * A SECOND reason, now HISTORICAL — CORRECTED (PR 7871 review thread 66483). `ApiBase.execute`'s
 * catch block used to do `result.status = error.response.status` with no guard, so a network
 * failure, CORS rejection or timeout — where axios throws an error with NO `.response` — threw a
 * TypeError straight OUT of `execute` and rejected the promise. That is FIXED: the catch is now
 * `error?.response?.status ?? 0`, and the returned-result contract holds.
 *
 * The wrappers below are deliberately KEPT as defence in depth — they still tag a malformed or
 * unexpected payload — but they are no longer the only thing preventing an unhandled rejection.
 * Do not restore the old claim.
 * ====================================================================================
 */

export type ApiResult<T> =
  | { kind: 'ok'; data: T }
  /** 401 — no/expired token. The shell handles re-auth; screens just say so. */
  | { kind: 'unauthorized' }
  /** 403 — the server refused the call. A real and expected state here. */
  | { kind: 'forbidden' }
  | { kind: 'notFound'; message: string }
  /** 400/409 — the server rejected the request with a reason worth showing verbatim. */
  | { kind: 'rejected'; status: number; message: string }
  /** 5xx, network failure, CORS, timeout, malformed payload. */
  | { kind: 'error'; status: number | null; message: string };

const TEMPLATES_URL = `${ConfigUrl.baseURL}/message-templates`;

/**
 * AUTHORIZATION — how the provider identity reaches this API.
 *
 * Every endpoint on `MessageTemplateApi` is gated by the module-wide
 * `anyRequest().authenticated()` rule in `SecurityConfiguration` — gmscloud-provider-api has
 * exactly one provider-admin principal by design, so there is no further per-user authority check
 * (the earlier `PRV.ADM` / `provider_user_authority` grant table was removed). The browser sends
 * the OIDC bearer token from `useOidcAccessToken()` (the same token every other service in this app
 * sends); the server only needs to confirm it is authenticated at all.
 *
 * `forbidden` is kept as a first-class result kind — rendered as its own state rather than an
 * empty queue — so any future 403 still surfaces as a refusal, not silence.
 */
const classify = (res: { ok: boolean; status: number; message?: string }): ApiResult<never> => {
  switch (res.status) {
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'forbidden' };
    case 404:
      return { kind: 'notFound', message: res.message || 'Not found' };
    case 400:
    case 409:
      return { kind: 'rejected', status: res.status, message: res.message || 'The server rejected this request' };
    default:
      return {
        kind: 'error',
        status: typeof res.status === 'number' ? res.status : null,
        message: res.message || 'The request failed',
      };
  }
};

/** Wraps an ApiBase call so a thrown transport error becomes a tagged error, never a throw. */
const guard = async <T>(call: () => Promise<any>, extract: (data: any) => T): Promise<ApiResult<T>> => {
  let res: any;
  try {
    res = await call();
  } catch (thrown: any) {
    // Reached when ApiBase's own catch block throws (no `error.response`) — i.e. the
    // network never answered. Distinct from any HTTP status the server chose to send.
    return {
      kind: 'error',
      status: null,
      message: thrown?.message
        ? `Could not reach provider-api: ${thrown.message}`
        : 'Could not reach provider-api',
    };
  }
  if (res == null) {
    return { kind: 'error', status: null, message: 'provider-api returned no response' };
  }
  if (!res.ok) {
    return classify(res) as ApiResult<T>;
  }
  return { kind: 'ok', data: extract(res.data) };
};

/** Defensive: a 200 whose payload is not an array is a server contract break, not "empty". */
const asArray = <T>(data: any): T[] => {
  if (Array.isArray(data)) return data as T[];
  throw new TypeError('Expected a JSON array from the review queue');
};

/**
 * GET /api/v1/message-templates
 *
 * Returns UNDER_REVIEW submissions ONLY, ordered OLDEST FIRST by `submitted_at`
 * (`MessageTemplateMasterRepository.findReviewQueue`). The controller rejects any
 * `?status=` other than UNDER_REVIEW with a 400 rather than silently returning the
 * wrong set, so no status parameter is sent.
 *
 * The oldest-first ordering is the SERVER's and is preserved verbatim — screens must not
 * re-sort this list. `TemplateSummaryDTO` carries no timestamp, so the order is the only
 * surviving evidence of age (see the gap note in `ReviewQueue.tsx`).
 */
export const fetchReviewQueue = async (accessToken: string): Promise<ApiResult<TemplateSummary[]>> => {
  let result: ApiResult<TemplateSummary[]>;
  try {
    result = await guard<TemplateSummary[]>(
      () => executeGetData(TEMPLATES_URL, accessToken),
      (data) => asArray<TemplateSummary>(data),
    );
  } catch (thrown: any) {
    return { kind: 'error', status: null, message: thrown?.message ?? 'Malformed review queue payload' };
  }
  return result;
};

/**
 * POST /api/v1/message-templates
 *
 * Provider-authored template — no human review, ever (R2). What happens after saving now
 * depends on which case this call is (2026-08-05 draft/publish work):
 *
 * `templateMasterUid` SELECTS THE SERVER PATH and is not decoration (T50, fixed 2026-08-04;
 * the controller previously discarded it). OMITTED (a brand-new template): the controller
 * runs createDraft only — the 201 response is a DRAFT, not live; `publishTemplate` is the
 * separate, explicit call that makes it live. PRESENT (a revision of an existing family):
 * the controller runs proposeChange -> approve -> publish, versioning the EXISTING family —
 * so sending a stale uid does not create a duplicate, it publishes an unintended new version
 * of a live template — and the 201 response IS already live for every tenant, same as before.
 * It is deleted from the payload when null so the two cases stay unambiguous on the wire.
 *
 * The response's `version` is the server's (proposeChange takes MAX(version) + 1 across the
 * whole family); callers must read it rather than compute it.
 *
 * `subject` is stripped for SMS: the DB CHECK `chk_template_subject_by_channel` forbids a
 * subject on an SMS row, and sending an empty string would trip it.
 */
export const createTemplateAsProvider = async (
  submission: TemplateSubmit,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> => {
  const payload: TemplateSubmit = { ...submission };
  if (payload.subject == null || payload.subject.trim() === '') {
    delete payload.subject;
  }
  if (payload.templateMasterUid == null) {
    delete payload.templateMasterUid;
  }
  return guard<TemplateSummary>(
    () => executePostData(TEMPLATES_URL, payload, accessToken),
    (data) => data as TemplateSummary,
  );
};

/**
 * POST /api/v1/message-templates/{uid}/approve[?version=]
 *
 * Approve EXACTLY as submitted — there is no edit-then-approve path, and this endpoint
 * accepts no name/subject/body. `version` is passed explicitly whenever the caller knows
 * it (these screens always do, from the queue row), because omitting it makes the server
 * fall back to the oldest pending submission for that template — which is not necessarily
 * the one the reviewer was looking at when more than one tenant has a request in flight
 * (B11).
 *
 * Approving also AUTO-REJECTS every other UNDER_REVIEW submission for the same
 * templateMasterUid (decided 2026-07-29). Review Detail warns about this before the click.
 */
export const approveTemplate = async (
  templateMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () => executePostData(`${TEMPLATES_URL}/${templateMasterUid}/approve?version=${version}`, null, accessToken),
    (data) => data as TemplateSummary,
  );

/**
 * POST /api/v1/message-templates/{uid}/reject[?version=]  — body TemplateReviewDTO.
 *
 * `reviewNotes` is REQUIRED (400 otherwise, and a DB CHECK behind that). The UI enforces
 * it too, so the reviewer finds out before losing their typing rather than after.
 *
 * `decision` is sent because `TemplateReviewDTO` declares it `@NotBlank`. The reject
 * handler happens not to annotate its body `@Valid` today, so it is not enforced on this
 * path — sending it anyway keeps the payload honest to the declared DTO and survives that
 * annotation being added.
 */
export const rejectTemplate = async (
  templateMasterUid: string,
  version: number,
  reviewNotes: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () =>
      executePostData(
        `${TEMPLATES_URL}/${templateMasterUid}/reject?version=${version}`,
        { decision: 'REJECT', reviewNotes },
        accessToken,
      ),
    (data) => data as TemplateSummary,
  );

/**
 * POST /api/v1/message-templates/{uid}/request-changes[?version=] — body TemplateReviewDTO.
 * Same reviewNotes requirement as reject; sends the submission back for rework instead of
 * killing it terminally.
 */
export const requestTemplateChanges = async (
  templateMasterUid: string,
  version: number,
  reviewNotes: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () =>
      executePostData(
        `${TEMPLATES_URL}/${templateMasterUid}/request-changes?version=${version}`,
        { decision: 'REQUEST_CHANGES', reviewNotes },
        accessToken,
      ),
    (data) => data as TemplateSummary,
  );

/**
 * POST /api/v1/message-templates/{uid}/retire — 204 on success, 409 while any Provider-DB
 * plan step still references the template.
 */
export const retireTemplate = async (
  templateMasterUid: string,
  accessToken: string,
): Promise<ApiResult<void>> =>
  guard<void>(
    () => executePostData(`${TEMPLATES_URL}/${templateMasterUid}/retire`, null, accessToken),
    () => undefined,
  );

/**
 * POST /api/v1/message-templates/{uid}/clone — body `{ name }` only; channel/subject/body are
 * copied server-side from the source's own LIVE (APPROVED) version. 201 with the newly created
 * template (a genuinely new templateMasterUid, version 1, status DRAFT) on success; 404 if the
 * source template has no live version to clone from.
 */
export const cloneTemplate = async (
  templateMasterUid: string,
  name: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () => executePostData(`${TEMPLATES_URL}/${templateMasterUid}/clone`, { name }, accessToken),
    (data) => data as TemplateSummary,
  );

/**
 * GET /api/v1/message-templates/{uid}/usage
 *
 * Reads PR-09g's materialized cross-tenant usage snapshot — NOT a live count (see
 * `TemplateUsage.refreshedAt`). 404 if the templateMasterUid does not exist.
 */
export const fetchTemplateUsage = async (
  templateMasterUid: string,
  accessToken: string,
): Promise<ApiResult<TemplateUsage>> =>
  guard<TemplateUsage>(
    () => executeGetData(`${TEMPLATES_URL}/${templateMasterUid}/usage`, accessToken),
    (data) => data as TemplateUsage,
  );

// GET /api/v1/message-templates/categories
export const fetchTemplateCategories = async (
  accessToken: string,
): Promise<ApiResult<TemplateCategoryOption[]>> =>
  guard<TemplateCategoryOption[]>(
    () => executeGetData(`${TEMPLATES_URL}/categories`, accessToken),
    (data) => data as TemplateCategoryOption[],
  );

/**
 * POST /api/v1/message-templates/{uid}/versions/{version}/preview
 *
 * Server-rendered preview of a specific version — sample-token substitution + sanitization via
 * the shared MessagePreviewRenderer (same renderer the tenant preview uses). Read-only: renders
 * nothing to the DB. Sends an empty variables map, so every catalogued token renders with its
 * sample value.
 */
export const previewTemplateVersion = async (
  templateMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<PreviewResponse>> =>
  guard<PreviewResponse>(
    () => executePostData(`${TEMPLATES_URL}/${templateMasterUid}/versions/${version}/preview`, {}, accessToken),
    (data) => data as PreviewResponse,
  );

/**
 * GET /api/v1/message-templates/{uid}/versions
 *
 * Version history — every non-deleted version of the family, any status, newest first
 * (Comms PR-22, LLD §7.2's 2026-08-04 correction). This is the PROVIDER-side endpoint;
 * do not confuse it with the separate, tenant-visibility-scoped `.../versions` read on
 * `CommunicationTemplateApi` (tenant-api), which this screen does not call. 404 if the
 * templateMasterUid does not exist (or has no non-deleted version at all).
 */
export const fetchTemplateVersions = async (
  templateMasterUid: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary[]>> => {
  let result: ApiResult<TemplateSummary[]>;
  try {
    result = await guard<TemplateSummary[]>(
      () => executeGetData(`${TEMPLATES_URL}/${templateMasterUid}/versions`, accessToken),
      (data) => asArray<TemplateSummary>(data),
    );
  } catch (thrown: any) {
    return { kind: 'error', status: null, message: thrown?.message ?? 'Malformed version history payload' };
  }
  return result;
};

/**
 * GET /api/v1/message-templates/manage
 *
 * The provider-admin "manage templates" list (2026-08-05 draft/publish work) — DRAFT,
 * UNDER_REVIEW, APPROVED, CHANGES_REQUESTED, REJECTED (both added 2026-08-06 — see
 * MessageTemplateMasterRepository#findManageCatalog's javadoc for why each was a gap), and
 * PUBLISHED rows, name then version order. This is what closes the gap the note below used
 * to describe: Template List's Email/SMS tabs now read from here instead of the
 * UNDER_REVIEW-only review queue, so a provider can find their own drafts, pending/sent-
 * back-for-rework/rejected submissions, and already-published templates to revise or
 * publish, not just pending submissions.
 *
 * <p><strong>NOT the tenant-facing live catalog.</strong> `MessageTemplateMasterService
 * .liveCatalog()` (PUBLISHED-only) is a SEPARATE method this function does not call and
 * must not be confused with — that one backs tenant-facing template selection elsewhere
 * (Automation Plan step authoring, tenant-web's own template browser) and stays untouched.
 */
export const fetchManageCatalog = async (accessToken: string): Promise<ApiResult<TemplateSummary[]>> => {
  let result: ApiResult<TemplateSummary[]>;
  try {
    result = await guard<TemplateSummary[]>(
      () => executeGetData(`${TEMPLATES_URL}/manage`, accessToken),
      (data) => asArray<TemplateSummary>(data),
    );
  } catch (thrown: any) {
    return { kind: 'error', status: null, message: thrown?.message ?? 'Malformed manage-catalog payload' };
  }
  return result;
};

/**
 * POST /api/v1/message-templates/{uid}/publish?version= (2026-08-05 draft/publish work)
 *
 * Makes a template live for every tenant. A DRAFT or CHANGES_REQUESTED version is
 * submitted and approved first (no separate human review — same reasoning as
 * `createTemplateAsProvider`/R2, just deferred until the provider explicitly asks); an
 * already-APPROVED version (e.g. one a reviewer approved but never published) is
 * published directly. Always publishes to every tenant — there is no tenant-scoped option
 * on this endpoint. 409 if the version is not DRAFT/CHANGES_REQUESTED/APPROVED (already
 * published, superseded, or rejected).
 */
export const publishTemplate = async (
  templateMasterUid: string,
  version: number,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () => executePostData(`${TEMPLATES_URL}/${templateMasterUid}/publish?version=${version}`, null, accessToken),
    (data) => data as TemplateSummary,
  );

/**
 * PUT /api/v1/message-templates/{uid}/edit?version= — body `{ name, subject, body }`.
 *
 * Edits a provider-owned template IN PLACE — same version, no new row (2026-08-05 draft/
 * publish work). Only ever succeeds on a DRAFT/APPROVED/REJECTED/CHANGES_REQUESTED,
 * provider-owned version — never UNDER_REVIEW or PUBLISHED, since a row that has ever gone
 * live must be revised as a new version instead (`createTemplateAsProvider` with
 * `templateMasterUid` set), never mutated. 409 (`TEMPLATE_PROVIDER_EDIT_NOT_ELIGIBLE`) if the
 * version is not in that eligible set.
 *
 * `subject` is stripped for SMS, same rule as `createTemplateAsProvider`.
 */
export const editTemplate = async (
  templateMasterUid: string,
  version: number,
  fields: { name: string; subject: string | null; body: string },
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> => {
  const payload: { name: string; subject?: string | null; body: string } = { ...fields };
  if (payload.subject == null || payload.subject.trim() === '') {
    delete payload.subject;
  }
  return guard<TemplateSummary>(
    () => executePutData(`${TEMPLATES_URL}/${templateMasterUid}/edit?version=${version}`, payload, accessToken),
    (data) => data as TemplateSummary,
  );
};

/**
 * PATCH /api/v1/message-templates/{uid}/category?version= — body `{ category, isDefault }`.
 *
 * Metadata only — never gated by, and never gates, approve/publish eligibility (unlike
 * `editTemplate`, this succeeds on ANY non-deleted, provider-owned version regardless of status,
 * including PUBLISHED). Pass `category: null` to clear a template's category.
 */
// isDefault: omit/undefined leaves the flag untouched, true sets this as the channel+category
// default (clearing any prior default server-side), false un-defaults it.
export const setTemplateCategory = async (
  templateMasterUid: string,
  version: number,
  category: string | null,
  accessToken: string,
  isDefault?: boolean,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () =>
      executePatchData(
        `${TEMPLATES_URL}/${templateMasterUid}/category?version=${version}`,
        isDefault === undefined ? { category } : { category, isDefault },
        accessToken,
      ),
    (data) => data as TemplateSummary,
  );

// GET /api/v1/message-templates/default-holder?channel=&category= — the template currently
// holding the default for one channel+category. 'notFound' means no default is set yet.
export const fetchDefaultHolder = async (
  channel: number,
  category: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> =>
  guard<TemplateSummary>(
    () =>
      executeGetData(
        `${TEMPLATES_URL}/default-holder?channel=${channel}&category=${encodeURIComponent(category)}`,
        accessToken,
      ),
    (data) => data as TemplateSummary,
  );

/**
 * GET /api/v1/message-templates/catalog?channel={SMS|EMAIL}
 *
 * AB#26892 — the picker source for the GoPAL Trade Template Builder's `'Send eMail'` action rows
 * (`TaskInspector.tsx`). Per `MessageTemplateApi`'s own `@Operation` summary: "Usable templates for
 * a channel (APPROVED or PUBLISHED), latest per family — feeds the plan-builder template picker."
 *
 * <p><strong>Not the same set as the tenant-facing live catalog.</strong> This is
 * APPROVED-or-PUBLISHED (a superset), not PUBLISHED-only — deliberately, since it already exists
 * and is documented as the shared plan-builder picker source; there is no PUBLISHED-only
 * provider-api route today (see the NOT AVAILABLE note below for the one gap that IS tracked).
 * `TaskInspector.tsx` authors a shared trade template with no bound tenant/project, so there is
 * nothing to render a live preview against here — this function is picker-only, by design, not a
 * missing preview capability.
 */
export const fetchTemplateCatalog = async (
  channel: 'SMS' | 'EMAIL',
  accessToken: string,
): Promise<ApiResult<TemplateSummary[]>> => {
  let result: ApiResult<TemplateSummary[]>;
  try {
    result = await guard<TemplateSummary[]>(
      () => executeGetData(`${TEMPLATES_URL}/catalog?channel=${channel}`, accessToken),
      (data) => asArray<TemplateSummary>(data),
    );
  } catch (thrown: any) {
    return { kind: 'error', status: null, message: thrown?.message ?? 'Malformed template catalog payload' };
  }
  return result;
};

/**
 * GET /api/v1/message-templates/{uid}/live — the LIVE version of one template (highest-numbered
 * PUBLISHED, non-deleted). Added 2026-08-13 (Comms gap-fix #1): the endpoint the block here used to
 * record as "NOT AVAILABLE" now exists on `MessageTemplateApi.live`, wrapping the single liveness
 * resolver `MessageTemplateMasterService.findLive(uid)`. Review Detail can read the current live
 * body to diff a proposed revision against, instead of scanning the whole `/versions` list.
 *
 * <p>404 when no PUBLISHED version exists yet (a brand-new template's first submission has none) —
 * the caller must treat that as "no current version to diff against", NOT as an error, exactly as
 * `diffTemplates(null, proposed)` already handles a null base.
 *
 * <p>Note `GET /api/v1/sms/templates` is NOT a substitute: it reads the legacy `sms_template` table
 * via `SmsTemplateDTO`, a different table from `message_template_master`.
 */
export const fetchLiveTemplate = async (
  templateMasterUid: string,
  accessToken: string,
): Promise<ApiResult<TemplateSummary>> => {
  try {
    return await guard<TemplateSummary>(
      () => executeGetData(`${TEMPLATES_URL}/${templateMasterUid}/live`, accessToken),
      (data) => data as TemplateSummary,
    );
  } catch (thrown: any) {
    return { kind: 'error', status: null, message: thrown?.message ?? 'Malformed live-template payload' };
  }
};
